import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

export type OverlayId = 
  | 'terrainStats'
  | 'terrainConfig'
  | 'operations'
  | 'searchMode'
  | 'addMenu'
  | 'environmentMenu'
  | 'mentionMenu'
  | string;

interface InputOverlayContextValue {
  activeOverlay: OverlayId | null;
  isOverlayOpen: (id: OverlayId) => boolean;
  openOverlay: (id: OverlayId) => void;
  closeOverlay: (id: OverlayId) => void;
  toggleOverlay: (id: OverlayId) => void;
  closeAllOverlays: () => void;
}

const InputOverlayContext = createContext<InputOverlayContextValue | null>(null);

export function useInputOverlayContext() {
  const context = useContext(InputOverlayContext);
  if (!context) {
    throw new Error('useInputOverlayContext must be used within InputOverlayProvider');
  }
  return context;
}

export function useInputOverlayContextOptional() {
  return useContext(InputOverlayContext);
}

interface InputOverlayProviderProps {
  children: React.ReactNode;
  onOverlayChange?: (overlayId: OverlayId | null) => void;
}

export function InputOverlayProvider({ 
  children, 
  onOverlayChange 
}: InputOverlayProviderProps) {
  const [activeOverlay, setActiveOverlay] = useState<OverlayId | null>(null);

  const isOverlayOpen = useCallback((id: OverlayId): boolean => {
    return activeOverlay === id;
  }, [activeOverlay]);

  const openOverlay = useCallback((id: OverlayId) => {
    setActiveOverlay(id);
    onOverlayChange?.(id);
  }, [onOverlayChange]);

  const closeOverlay = useCallback((id: OverlayId) => {
    setActiveOverlay(prev => {
      if (prev === id) {
        onOverlayChange?.(null);
        return null;
      }
      return prev;
    });
  }, [onOverlayChange]);

  const toggleOverlay = useCallback((id: OverlayId) => {
    setActiveOverlay(prev => {
      const newValue = prev === id ? null : id;
      onOverlayChange?.(newValue);
      return newValue;
    });
  }, [onOverlayChange]);

  const closeAllOverlays = useCallback(() => {
    setActiveOverlay(null);
    onOverlayChange?.(null);
  }, [onOverlayChange]);

  const value = useMemo<InputOverlayContextValue>(() => ({
    activeOverlay,
    isOverlayOpen,
    openOverlay,
    closeOverlay,
    toggleOverlay,
    closeAllOverlays
  }), [activeOverlay, isOverlayOpen, openOverlay, closeOverlay, toggleOverlay, closeAllOverlays]);

  return (
    <InputOverlayContext.Provider value={value}>
      {children}
    </InputOverlayContext.Provider>
  );
}

